import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  checkUserExistenceApi,
  createLoyaltyCustomerApi,
  createUserApi,
  createUserDeliveryAddressApi,
  facebookLoginApi,
  fetchOcaUserApi,
  fetchUserViaOddlePassApi,
  getOPAccessTokenApi,
  getOPAddressesApi,
  getUserApi,
  removeUserDeliveryAddressApi,
  resendOTPApi,
  sendOTPApi,
  setDefaultUserDeliveryAddressApi,
  updateUserApi,
  updateUserDeliveryAddressApi,
  updateUserPasswordApi,
  userLoginApi,
  userLogoutApi,
  userResetPasswordApi,
  verifyOTPApi
} from 'src/services/oms'
import type { UserStateInReducer } from './slice'
import type { MetaStateInReducer } from '../meta'

export const getOPAccessTokenAction = createAsyncThunk(
  '@user/getOPAccessToken',
  async ({ menuId, customerId }: { customerId: string; menuId: string }) => {
    const result = await getOPAccessTokenApi({ customerId, menuId })
    return result.data
  }
)

export const loginAction = createAsyncThunk(
  '@user/login',
  async (
    {
      email,
      password,
      source
    }: { email: string; password: string; source: string },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { storeOrderId, menuId }
      }
    } = state['@meta']
    const result = await userLoginApi({
      cartName,
      email,
      password,
      source,
      storeOrderId,
      menuId
    })
    return result.data
  }
)

export const fetchOcaUserAction = createAsyncThunk(
  '@user/fetchOcaUser',
  async (
    { email, password }: { email: string; password: string },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { menuId }
      }
    } = state['@meta']
    const result = await fetchOcaUserApi({
      cartName,
      email,
      password,
      menuId
    })
    return result.data
  }
)

export const logoutAction = createAsyncThunk(
  '@user/logout',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = state['@meta']
    const result = await userLogoutApi({
      cartName,
      storeOrderId
    })
    return result.data
  }
)

export const signUpAction = createAsyncThunk(
  '@user/signUp',
  async (
    {
      email,
      firstName,
      lastName,
      password,
      phone,
      facebookId,
      source,
      gender,
      dob,
      hash,
      receiveEmail,
      acceptMarketing,
      acceptMarketingMenu
    }: {
      email: string
      firstName: string
      lastName: string
      password: string
      phone: string
      facebookId: string
      source: string
      gender: string
      dob: string
      hash: string
      receiveEmail: boolean
      acceptMarketing: boolean
      acceptMarketingMenu: boolean
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { storeOrderId, menuId }
      }
    } = state['@meta']
    const result = await createUserApi({
      email,
      firstName,
      lastName,
      password,
      phone,
      facebookId,
      source,
      gender,
      dob,
      hash,
      receiveEmail,
      acceptMarketing,
      acceptMarketingMenu,
      cartName,
      storeOrderId,
      menuId
    })
    return result.data
  }
)

export const fetchUserFacebookAction = createAsyncThunk(
  '@user/fetchUserFacebook',
  async (
    {
      hash
    }: {
      hash: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { storeOrderId }
      }
    } = state['@meta']
    const result = await facebookLoginApi({
      cartName,
      storeOrderId,
      hash
    })
    return result.data
  }
)

export const fetchUserAction = createAsyncThunk(
  '@user/fetchUser',
  async (
    {
      clientId
    }: {
      clientId?: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      cartName,
      detailMenuSite: { mode }
    } = state['@meta']
    if (mode === 'preview')
      return {
        errorCode: 'store_order_not_found',
        errorMessage: 'store_order_not_found',
        errorStack: null,
        payload: {
          error: 'user_not_logged_in'
        },
        result: false
      }
    const result = await getUserApi({
      cartName,
      clientId
    })
    return result.data
  }
)

export const updateUserAction = createAsyncThunk(
  '@user/updateUser',
  async (
    {
      email,
      firstName,
      lastName,
      phone,
      organisationName,
      receiveEmail,
      salutation,
      additionalPhone,
      gender,
      dob,
      source,
      id: customerId,
      acceptMarketing,
      acceptMarketingMenu
    }: {
      email: string
      firstName: string
      lastName: string
      phone: string
      organisationName: string
      receiveEmail: boolean
      salutation
      additionalPhone: string
      gender
      dob: string
      source: string
      id: string
      acceptMarketing: boolean
      acceptMarketingMenu: boolean
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer & UserStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { menuId }
      }
    } = state['@meta']
    const result = await updateUserApi({
      payload: {
        email,
        salutation,
        firstName,
        lastName,
        phone,
        additionalPhone,
        organisationName,
        receiveEmail,
        gender,
        dob,
        source,
        customerId: state['@user'].user.data.id || customerId,
        menuId,
        acceptMarketing,
        acceptMarketingMenu
      },
      cartName
    })
    return result.data
  }
)

export const updateUserPasswordAction = createAsyncThunk(
  '@user/updateUserPassword',
  async (
    {
      customerId,
      menuId,
      newPassword,
      reNewPassword,
      password
    }: {
      customerId: string
      menuId: string
      newPassword: string
      reNewPassword: string
      password: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const { cartName } = state['@meta']
    const result = await updateUserPasswordApi({
      cartName,
      customerId,
      menuId,
      newPassword,
      reNewPassword,
      password
    })
    return result.data
  }
)

export const resetPasswordAction = createAsyncThunk(
  '@user/resetPassword',
  async (
    {
      email
    }: {
      email: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { menuId }
      }
    } = state['@meta']
    const result = await userResetPasswordApi({ cartName, email, menuId })
    return result.data
  }
)

export const setDefaultUserDeliveryAddressAction = createAsyncThunk(
  '@user/setDefaultUserDeliveryAddress',
  async (addressId: string, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer & UserStateInReducer
    const {
      detailMenuSite: {
        data: { menuId, oddlePassEnabled, newOddlePassFlow, enableOcaAddress }
      }
    } = state['@meta']

    const isEnableOcaAddress =
      oddlePassEnabled && newOddlePassFlow && enableOcaAddress
    const customerId = isEnableOcaAddress
      ? state['@user'].user.data.ocaId
      : state['@user'].user.data.id
    if (isEnableOcaAddress) {
      return window.opw.setReferredDeliveryAddress(addressId, customerId)
    }
    const result = await setDefaultUserDeliveryAddressApi({
      customerId,
      addressId,
      menuId
    })
    return result.data
  }
)

export const createUserDeliveryAddressAction = createAsyncThunk(
  '@user/createUserDeliveryAddress',
  async (
    {
      address
    }: {
      address: {
        autoCompleteAddress: string
        address: string
        addressTwo: string
        city: string
        state: string
        country: string
        postal: string
        latitude: string
        longitude: string
      }
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer & UserStateInReducer
    const {
      cartName,
      detailMenuSite: {
        data: { oddlePassEnabled, newOddlePassFlow, enableOcaAddress }
      }
    } = state['@meta']

    const isEnableOcaAddress =
      oddlePassEnabled && newOddlePassFlow && enableOcaAddress
    const customerId = isEnableOcaAddress
      ? state['@user'].user.data.ocaId
      : state['@user'].user.data.id
    const result = isEnableOcaAddress
      ? await window.opw.createNewAddress(customerId, address)
      : await createUserDeliveryAddressApi({ cartName, customerId, address })
    return result.data
  }
)

export const updateUserDeliveryAddressAction = createAsyncThunk(
  '@user/updateUserDeliveryAddress',
  async (
    {
      id,
      autoCompleteAddress,
      addressTwo,
      city,
      state,
      country,
      postal,
      latitude,
      longitude
    }: {
      id: string
      autoCompleteAddress: string
      addressTwo: string
      city: string
      state: string
      country: string
      postal: string
      latitude: string
      longitude: string
    },
    thunkApi
  ) => {
    const rootState = thunkApi.getState() as MetaStateInReducer &
      UserStateInReducer
    const {
      detailMenuSite: {
        data: { oddlePassEnabled, newOddlePassFlow, enableOcaAddress }
      }
    } = rootState['@meta']

    const isEnableOcaAddress =
      oddlePassEnabled && newOddlePassFlow && enableOcaAddress
    const customerId = isEnableOcaAddress
      ? rootState['@user'].user.data.ocaId
      : rootState['@user'].user.data.id
    const result = isEnableOcaAddress
      ? await window.opw.updateAddress(id, {
          id,
          autoCompleteAddress,
          addressTwo,
          city,
          state,
          country,
          postal,
          latitude,
          longitude,
          customerId
        })
      : await updateUserDeliveryAddressApi({
          customerId,
          address: {
            id,
            autoCompleteAddress,
            addressTwo,
            city,
            state,
            country,
            postal,
            latitude,
            longitude
          }
        })
    return result.data
  }
)

export const removeUserDeliveryAddressAction = createAsyncThunk(
  '@user/removeUserDeliveryAddress',
  async (
    {
      addressId
    }: {
      addressId: string
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer & UserStateInReducer
    const userState = state['@user']
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { oddlePassEnabled, newOddlePassFlow, enableOcaAddress }
      }
    } = metaState

    const isEnableOcaAddress =
      oddlePassEnabled && newOddlePassFlow && enableOcaAddress
    const customerId = isEnableOcaAddress
      ? userState.user.data.ocaId
      : userState.user.data.id

    if (isEnableOcaAddress) {
      return window.opw.deleteAddress(addressId)
    }

    const result = await removeUserDeliveryAddressApi({ customerId, addressId })
    return result.data
  }
)

export const checkUserEmailAction = createAsyncThunk(
  '@user/checkUserEmail',
  async (
    { userEmail, source }: { userEmail: string; source: string },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const {
      detailMenuSite: {
        data: { menuId }
      }
    } = metaState

    const result = await checkUserExistenceApi({
      userEmail,
      source,
      menuId
    })
    return result.data
  }
)

export const createThenSetUserDefaultAddressAction = createAsyncThunk(
  '@user/createThenSetUserDefaultAddress',
  async (
    {
      address
    }: {
      address: {
        autoCompleteAddress: string
        address: string
        addressTwo: string
        city: string
        state: string
        country: string
        postal: string
        latitude: string
        longitude: string
      }
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as UserStateInReducer
    const userState = state['@user']
    const createResponse = await thunkApi.dispatch(
      createUserDeliveryAddressAction({ address })
    )

    if (createResponse.meta.requestStatus === 'fulfilled') {
      const response = await thunkApi
        .dispatch(
          setDefaultUserDeliveryAddressAction(createResponse.payload.id)
        )
        .unwrap()
      return response
    }
  }
)

export const verifyOTPAction = createAsyncThunk(
  '@user/verifyOTP',
  async (
    { OTP, OTPSessionId, phone }: { OTP; OTPSessionId; phone },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      detailMenuSite: {
        data: { menuId }
      }
    } = state['@meta']
    const result = await verifyOTPApi({
      OTP,
      OTPSessionId,
      phone,
      menuId
    })
    return result.data
  }
)

export const resendOTPAction = createAsyncThunk(
  '@user/resendOTP',
  async ({ OTPSessionId }: { OTPSessionId }, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      detailMenuSite: {
        data: { menuId }
      }
    } = state['@meta']
    const result = await resendOTPApi({ OTPSessionId, menuId })
    return result.data
  }
)

export const sendOTPAction = createAsyncThunk(
  '@user/sendOTP',
  async (
    {
      phone,
      purpose = 'API_UPDATE_MEMBERSHIP'
    }: { phone: string; purpose?: string },
    thunkApi
  ) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      detailMenuSite: {
        data: { menuId }
      }
    } = state['@meta']
    const result = await sendOTPApi({
      purpose,
      phone,
      menuId
    })
    return result.data
  }
)

export const createLoyaltyCustomerAction = createAsyncThunk(
  '@user/createLoyaltyCustomer',
  async ({ customerId }: { customerId: string }, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const {
      detailMenuSite: {
        data: { menuId }
      }
    } = state['@meta']
    const result = await createLoyaltyCustomerApi({
      customerId,
      menuId
    })
    return result.data
  }
)

export const oddlePassV5LoginAction = createAction(
  '@user/oddlePassV5Login',
  ({
    id,
    firstName,
    lastName,
    email,
    phone,
    continueWithOddlePass
  }: {
    id: string
    firstName: string
    lastName: string
    email: string
    phone: string
    continueWithOddlePass: boolean
  }): {
    payload: {
      id: string
      firstName: string
      lastName: string
      email: string
      phone: string
      continueWithOddlePass: boolean
    }
  } => {
    return {
      payload: {
        id,
        firstName,
        lastName,
        email,
        phone,
        continueWithOddlePass
      }
    }
  }
)

export const oddlePassV5LogoutAction = createAction(
  '@user/oddlePassV5Logout',
  (): {
    payload: {
      success: boolean
    }
  } => {
    return {
      payload: {
        success: true
      }
    }
  }
)

export const fetchUserViaOddlePassAction = createAsyncThunk(
  '@user/fetchUserViaOddlePass',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const { cartName } = metaState

    const result = await fetchUserViaOddlePassApi({ cartName })
    return result.data
  }
)

export const getAllAddressesOPAction = createAsyncThunk(
  '@user/getAllAddressesOP',
  async () => {
    const result = await getOPAddressesApi()
    return result.data
  }
)

export const getAllSavedCardsAction = createAsyncThunk(
  '@user/getAllSavedCards',
  async () => {
    if (typeof window !== 'undefined' && window.opw) {
      const result = await window.opw.getAllSavedCards()
      return result.data
    }
    return []
  }
)

export const removeSavedCardAction = createAsyncThunk(
  '@user/removeSavedCard',
  async ({ paymentMethodId }: { paymentMethodId: string }) => {
    if (typeof window !== 'undefined' && window.opw) {
      const result = await window.opw.removeSavedCard(paymentMethodId)
      return result.data
    }
    return {}
  }
)

export const setCardDefaultAction = createAsyncThunk(
  '@user/setCardDefault',
  async ({ paymentMethodId }: { paymentMethodId: string }) => {
    if (typeof window !== 'undefined' && window.opw) {
      const result = await window.opw.setCardDefault(paymentMethodId)
      return result.data
    }
    return {}
  }
)

export const saveNewCardAction = createAsyncThunk(
  '@user/saveNewCard',
  async ({ paymentMethodId }: { paymentMethodId: string }) => {
    if (typeof window !== 'undefined' && window.opw) {
      return window.opw.saveNewCard(paymentMethodId)
    }
    return {}
  }
)

export const fetchUserAfterLogoutAction = createAsyncThunk(
  '@user/fetchUserAfterLogoutAction',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as MetaStateInReducer
    const metaState = state['@meta']
    const { cartName } = metaState

    const result = await fetchUserViaOddlePassApi({ cartName, loggedOut: true })
    return result.data
  }
)
